import { TURNSTILE_FAILED_ERROR } from 'fym-shared/src/music24/turnstile';
import { readM24Error } from '../../../music24/utils/parseM24LimitError';

/**
 * The kind of error the auth screen shows (design: onboarding g6).
 * - `disposable`: music24-api refuses a throwaway address (M24_DISPOSABLE_EMAIL).
 * - `invalidEmail`: music24-api refuses the address as not an email (M24_INVALID_EMAIL). The
 *   field says "That does not look like an email address." (QA 2026-10-28, brk-r2-1).
 * - `rateLimit`: the code request, the code check or the resend got TOO_MANY_REQUESTS (HTTP 429).
 * - `badCode`: the code check refused the code (BAD_REQUEST "Invalid or expired code").
 * - `turnstile`: music24-api refused the hidden Turnstile check of a code send (FORBIDDEN,
 *   `data.errorType = M24_TURNSTILE_FAILED`). The page asks the person to try again; the check
 *   already runs again for the next send.
 * - `other`: any other error. The screen shows the standard error alert.
 */
export type AuthErrorKind = 'disposable' | 'invalidEmail' | 'rateLimit' | 'badCode' | 'turnstile' | 'other';

function trpcCode(error: Error): string | null {
  const { data } = error as { data?: unknown };
  if (typeof data !== 'object' || data === null) {
    return null;
  }
  const { code } = data as { code?: unknown };
  return typeof code === 'string' ? code : null;
}

function trpcErrorType(error: Error): string | null {
  const { data } = error as { data?: unknown };
  if (typeof data !== 'object' || data === null) {
    return null;
  }
  const { errorType } = data as { errorType?: unknown };
  return typeof errorType === 'string' ? errorType : null;
}

export function authErrorKind(error: Error | null): AuthErrorKind | null {
  if (error === null) {
    return null;
  }
  if (readM24Error(error)?.code === 'M24_DISPOSABLE_EMAIL') {
    return 'disposable';
  }
  if (trpcErrorType(error) === 'M24_INVALID_EMAIL') {
    return 'invalidEmail';
  }
  if (trpcErrorType(error) === TURNSTILE_FAILED_ERROR) {
    return 'turnstile';
  }
  const code = trpcCode(error);
  if (code === 'TOO_MANY_REQUESTS') {
    return 'rateLimit';
  }
  if (code === 'BAD_REQUEST') {
    return 'badCode';
  }
  return 'other';
}

const SECONDS_PER_MINUTE = 60;

/**
 * The wait of a rate-limit error in whole minutes, rounded up (Ruling F5). music24-api sends
 * `data.retryAfterSeconds` with the login-code limits. Null for any other error and for an older
 * API that sends no wait: the screen then uses the copy with no number.
 */
export function rateLimitWaitMinutes(error: Error | null): number | null {
  if (error === null || authErrorKind(error) !== 'rateLimit') {
    return null;
  }
  const { data } = error as { data?: { retryAfterSeconds?: unknown } };
  const seconds = data?.retryAfterSeconds;
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds <= 0) {
    return null;
  }
  return Math.ceil(seconds / SECONDS_PER_MINUTE);
}
